import type React from "react";
import { useState, useRef, useEffect } from "react";
import {
  Mail,
  MailX,
  MessageCircle,
  MessageCircleOff,
  Check,
} from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import { DateUtils } from "@frontend/shared/presentation/utils/date.utils";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { i18n } from "@frontend/i18n";

export interface ConnectedActionsProps {
  readonly onEmail: () => Promise<boolean>;
  readonly onWhatsApp: () => void;
  readonly connectedDate: Date | null;

  readonly hasEmail: boolean;

  readonly hasPhone: boolean;

  readonly isContactInfoPending: boolean;
}

export function ConnectedActions({
  onEmail,
  onWhatsApp,
  connectedDate,
  hasEmail,
  hasPhone,
  isContactInfoPending,
}: ConnectedActionsProps): React.JSX.Element {
  const locale = i18n.getLocale();
  const formattedDate =
    connectedDate !== null ? DateUtils.formatDate(connectedDate, locale) : null;

  // Only reached once the contact answer is in: the pending branch renders
  // placeholders instead, so an absent channel here is a settled fact.
  const isEmailDisabled = !hasEmail;
  const isWhatsAppDisabled = !hasPhone;

  const isEmailNotShared = !hasEmail;
  const isPhoneNotShared = !hasPhone;

  const [isCopied, setIsCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect((): (() => void) => {
    return (): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const emailLabel = isCopied
    ? i18n.t("user.directory.toast.emailCopied")
    : isEmailNotShared
      ? i18n.t("user.directory.profile.cta.emailNotShared")
      : i18n.t("user.directory.profile.cta.email");

  const whatsAppLabel = isPhoneNotShared
    ? i18n.t("user.directory.profile.cta.whatsappNotShared")
    : i18n.t("user.directory.profile.cta.whatsapp");

  function handleEmailClick(): void {
    void onEmail()
      .then((success: boolean) => {
        if (success) {
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }
          setIsCopied(true);
          timerRef.current = setTimeout(() => {
            setIsCopied(false);
            timerRef.current = null;
          }, 5000);
        }
      })
      .catch((err: Error) => {
        applicationLogger.error(
          "Unexpected error in email click handler",
          "ConnectedActions.handleEmailClick",
          err,
        );
      });
  }

  return (
    <div
      data-testid="connected-actions"
      className="flex flex-col items-center gap-2"
    >
      {isContactInfoPending ? (
        // Placeholders, not the buttons in a loading skin: the labels are the
        // claim ("Open WhatsApp" vs "WhatsApp not shared") and neither is known
        // yet. Same h-11 pill geometry as the Buttons they stand in for, so the
        // fixed CTA bar does not move when the answer arrives.
        <div
          className="flex w-full gap-3"
          data-testid="connected-actions-pending"
        >
          <Skeleton className="h-11 flex-1 rounded-full" aria-hidden="true" />
          <Skeleton className="h-11 flex-1 rounded-full" aria-hidden="true" />
        </div>
      ) : (
        <div className="flex w-full gap-3">
          <Button
            onClick={handleEmailClick}
            variant="outline"
            className="min-w-0 flex-1 gap-2 text-foreground"
            size="lg"
            disabled={isEmailDisabled || isCopied}
            data-testid="copy-email-button"
          >
            {isCopied ? (
              <Check className="h-4 w-4" />
            ) : isEmailNotShared ? (
              <MailX className="h-4 w-4" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            <span className="truncate">{emailLabel}</span>
          </Button>
          <Button
            data-testid="whatsapp-button"
            onClick={onWhatsApp}
            variant="outline"
            className="min-w-0 flex-1 gap-2 text-foreground"
            size="lg"
            disabled={isWhatsAppDisabled}
          >
            {isPhoneNotShared ? (
              <MessageCircleOff className="h-4 w-4" />
            ) : (
              <MessageCircle className="h-4 w-4" />
            )}
            <span className="truncate">{whatsAppLabel}</span>
          </Button>
        </div>
      )}
      {formattedDate !== null && (
        <p className="text-pds-2xs text-center text-muted-foreground">
          {i18n.t("user.directory.profile.cta.connectedOn", {
            date: formattedDate,
          })}
        </p>
      )}
    </div>
  );
}
